import {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useUser, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { apiClient, setTokenGetter } from '@/api/axios';

// Type for Clerk user from useUser hook
type ClerkUser = ReturnType<typeof useUser>['user'];

export type UserType = 'petso' | 'petso-pro' | 'farmso' | 'admin';

export interface User {
  email: string;
  name: string;
  authMethod: string;
  walletAddress?: string;
  role?: string;
  type: UserType;
  clerkId: string;
  // For petso-pro
  businessName?: string;
  specialization?: string;
  location?: string;
  verified?: boolean;
  rating?: number;
  totalReviews?: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  clerkUser: ClerkUser;
  isLoading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to map Clerk user to our User type
function mapClerkUserToUser(
  clerkUser: ClerkUser,
  userType: UserType = 'petso'
): User | null {
  if (!clerkUser) return null;

  const primaryEmail = clerkUser.primaryEmailAddress?.emailAddress || '';
  const fullName =
    clerkUser.fullName ||
    `${clerkUser.firstName || ''} ${clerkUser.lastName || ''}`.trim() ||
    primaryEmail;

  // Get user type from public metadata or default to 'petso'
  const metadata = clerkUser.publicMetadata as Record<string, unknown>;
  const type = (metadata?.userType as UserType) || userType;

  return {
    email: primaryEmail,
    name: fullName,
    authMethod: clerkUser.externalAccounts?.[0]?.provider || 'email',
    walletAddress: metadata?.walletAddress as string | undefined,
    role: metadata?.role as string | undefined,
    type,
    clerkId: clerkUser.id,
    businessName: metadata?.businessName as string | undefined,
    specialization: metadata?.specialization as string | undefined,
    location: metadata?.location as string | undefined,
    verified: metadata?.verified as boolean | undefined,
    rating: metadata?.rating as number | undefined,
    totalReviews: metadata?.totalReviews as number | undefined,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user: clerkUser, isLoaded: isClerkLoaded } = useUser();
  const { isSignedIn, getToken, signOut } = useClerkAuth();
  const hasSyncedRef = useRef<Set<string>>(new Set());
  const isSyncingRef = useRef(false);

  // Logout function using Clerk
  const logout = async () => {
    try {
      await signOut();
      hasSyncedRef.current.clear();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Set up the token getter for axios synchronously during render
  // so it's available before children's effects fire API calls.
  // (useEffect runs bottom-up: children first, then parent — too late.)
  setTokenGetter(async () => {
    try {
      return await getToken();
    } catch {
      return null;
    }
  });

  const user = useMemo(() => {
    if (!isClerkLoaded) return null;
    return mapClerkUserToUser(clerkUser);
  }, [clerkUser, isClerkLoaded]);

  // Sync user to database after login
  useEffect(() => {
    if (!isClerkLoaded || !isSignedIn || !clerkUser) {
      return;
    }

    const userId = clerkUser.id;

    // Prevent duplicate syncs for the same user session
    if (hasSyncedRef.current.has(userId) || isSyncingRef.current) {
      return;
    }

    // Sync user to database with a small delay to ensure token is ready
    const syncUser = async (attempt = 0) => {
      isSyncingRef.current = true;
      const MAX_SYNC_RETRIES = 3;

      try {
        // Wait a bit for the session to be fully established
        await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 500 : 1000 * attempt));

        // Verify we still have a valid session
        const token = await getToken();
        if (!token) {
          console.warn('No token available for sync, skipping...');
          isSyncingRef.current = false;
          return;
        }

        const metadata = clerkUser.publicMetadata as Record<string, unknown>;

        await apiClient.post('/pro/users/sync', {
          userType: metadata?.userType || user?.type,
          walletAddress: metadata?.walletAddress || user?.walletAddress,
          businessName: metadata?.businessName || user?.businessName,
          specialization: metadata?.specialization || user?.specialization,
          location: metadata?.location || user?.location,
        });

        hasSyncedRef.current.add(userId);
        console.log('User synced to database successfully');
      } catch (error) {
        console.error('Failed to sync user to database:', error);
        // Retry with back-off on failure
        if (attempt < MAX_SYNC_RETRIES) {
          isSyncingRef.current = false;
          syncUser(attempt + 1);
          return;
        }
        // Don't block the user experience if sync fails
        // The sync can be retried on next login
      } finally {
        isSyncingRef.current = false;
      }
    };

    syncUser();
  }, [isClerkLoaded, isSignedIn, clerkUser, user, getToken]);

  // Clear sync tracking when user logs out
  useEffect(() => {
    if (!isSignedIn) {
      hasSyncedRef.current.clear();
    }
  }, [isSignedIn]);

  const value: AuthContextType = {
    user,
    isAuthenticated: isSignedIn ?? false,
    clerkUser,
    isLoading: !isClerkLoaded,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
